import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Music, Plus, CreditCard, BarChart3, HelpCircle, ChevronRight, TrendingUp, Wallet, Clock, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const lang = user?.language || 'it';

  useEffect(() => {
    async function load() {
      try {
        const { data: d } = await api.get('/users/dashboard');
        setData(d);
      } catch (err) {
        toast.error('Errore caricamento dashboard');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E94560]" /></div>;

  const bal = data?.balance || {};
  const statusColors = { draft: 'bg-gray-100 text-gray-600', in_review: 'bg-yellow-100 text-yellow-700', approved: 'bg-blue-100 text-blue-700', rejected: 'bg-red-100 text-red-700', distributed: 'bg-green-100 text-green-700', takedown_requested: 'bg-orange-100 text-orange-700', removed: 'bg-gray-200 text-gray-600', modification_requested: 'bg-purple-100 text-purple-700' };
  const statusLabels = { draft: lang === 'it' ? 'Bozza' : 'Draft', in_review: lang === 'it' ? 'In Revisione' : 'In Review', approved: lang === 'it' ? 'Approvata' : 'Approved', rejected: lang === 'it' ? 'Rifiutata' : 'Rejected', distributed: '🟢 Live', takedown_requested: 'Take Down', removed: lang === 'it' ? 'Rimossa' : 'Removed', modification_requested: lang === 'it' ? 'Modifica' : 'Modification' };

  return (
    <div>
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E]">
          {lang === 'it' ? 'Ciao' : 'Hi'}, {user?.first_name}! 👋
        </h1>
        <p className="text-gray-500 mt-1">
          {lang === 'it' ? 'Ecco il riepilogo della tua attività' : "Here's your activity summary"}
        </p>
      </div>

      {/* Balance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-[#1A1A2E] to-[#0F3460] rounded-xl p-5 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={18} className="text-[#E94560]" />
            <span className="text-sm opacity-80">{lang === 'it' ? 'Saldo Disponibile' : 'Available Balance'}</span>
          </div>
          <p className="text-3xl font-bold">€{parseFloat(bal.available_balance || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={18} className="text-green-500" />
            <span className="text-sm text-gray-500">{lang === 'it' ? 'Totale Guadagnato' : 'Total Earned'}</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A2E]">€{parseFloat(bal.total_earned || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard size={18} className="text-blue-500" />
            <span className="text-sm text-gray-500">{lang === 'it' ? 'Totale Pagato' : 'Total Paid'}</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A2E]">€{parseFloat(bal.total_paid || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} className="text-orange-500" />
            <span className="text-sm text-gray-500">{lang === 'it' ? 'In Attesa Payout' : 'Pending Payout'}</span>
          </div>
          <p className="text-2xl font-bold text-[#1A1A2E]">€{parseFloat(bal.pending_payout || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Link to="/releases/new" className="flex items-center gap-3 bg-[#E94560] text-white rounded-xl p-4 hover:bg-[#d63b55] transition">
          <Plus size={20} /><span className="font-medium text-sm">{lang === 'it' ? 'Nuova Release' : 'New Release'}</span>
        </Link>
        <Link to="/artists" className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:border-[#E94560] transition">
          <Music size={20} className="text-[#E94560]" /><span className="font-medium text-sm">{lang === 'it' ? 'I Miei Artisti' : 'My Artists'}</span>
        </Link>
        <Link to="/streaming" className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:border-[#E94560] transition">
          <BarChart3 size={20} className="text-[#0F3460]" /><span className="font-medium text-sm">{lang === 'it' ? 'Ascolti' : 'Stats'}</span>
        </Link>
        <Link to="/support" className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:border-[#E94560] transition">
          <HelpCircle size={20} className="text-gray-500" />
          <span className="font-medium text-sm">{lang === 'it' ? 'Supporto' : 'Support'}
            {data?.unread_messages > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs bg-[#E94560] text-white rounded-full">{data.unread_messages}</span>}
          </span>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Streaming chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1A1A2E]">{lang === 'it' ? 'Ascolti Ultimi 30 Giorni' : 'Streams Last 30 Days'}</h2>
            <Link to="/streaming" className="text-sm text-[#E94560] hover:underline flex items-center gap-1">
              {lang === 'it' ? 'Dettagli' : 'Details'} <ChevronRight size={14} />
            </Link>
          </div>
          {data?.streaming_chart?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.streaming_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="stat_date" tick={{ fontSize: 10 }} tickFormatter={d => d.split('-').slice(1).join('/')} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="total_streams" stroke="#E94560" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              {lang === 'it' ? 'Nessun dato di streaming disponibile' : 'No streaming data available'}
            </div>
          )}
        </div>

        {/* Recent releases */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1A1A2E]">{lang === 'it' ? 'Release Recenti' : 'Recent Releases'}</h2>
            <Link to="/releases" className="text-sm text-[#E94560] hover:underline flex items-center gap-1">
              {lang === 'it' ? 'Tutte' : 'All'} <ChevronRight size={14} />
            </Link>
          </div>
          <div className="space-y-3">
            {data?.recent_releases?.length > 0 ? data.recent_releases.map(r => (
              <Link key={r.id} to={`/releases/${r.id}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition">
                <div className="w-10 h-10 rounded-lg bg-gray-200 flex-shrink-0 overflow-hidden">
                  {r.cover_art_path ? <img src={`/uploads/artwork/${user?.id}/${r.cover_art_path}`} alt="" className="w-full h-full object-cover" /> : <Music size={16} className="m-auto mt-3 text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.title}</p>
                  <p className="text-xs text-gray-400">{r.release_artist_names || r.label_name} · {r.release_type}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${statusColors[r.status] || 'bg-gray-100'}`}>
                  {statusLabels[r.status] || r.status}
                </span>
              </Link>
            )) : (
              <div className="text-center py-6 text-gray-400 text-sm">
                <Music size={24} className="mx-auto mb-2 opacity-50" />
                <p>{lang === 'it' ? 'Nessuna release ancora' : 'No releases yet'}</p>
                <Link to="/releases/new" className="text-[#E94560] font-medium hover:underline">
                  {lang === 'it' ? 'Crea la prima!' : 'Create your first!'}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Announcements */}
      {data?.announcements?.length > 0 && (
        <div className="mt-6 bg-blue-50 rounded-xl p-5 border border-blue-100">
          <h3 className="font-bold text-blue-800 mb-2">{lang === 'it' ? 'Comunicazioni' : 'Announcements'}</h3>
          {data.announcements.map(a => (
            <div key={a.id} className="mb-2 last:mb-0">
              <p className="text-sm font-medium text-blue-900">{lang === 'it' ? a.title_it : (a.title_en || a.title_it)}</p>
              <p className="text-xs text-blue-700">{lang === 'it' ? a.content_it : (a.content_en || a.content_it)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
